import { BasketballStandingsPage } from "./BasketballStandingsPage";
import { ConferencesPage } from "./ConferencesPage";
import { FootballStandingsPage } from "./FootballStandingsPage";
import { HomePage } from "./HomePage";
import { RPIPage } from "./RPIPage";
import { ScoreboardPage } from "./ScoreboardPage";
import { TeamPage } from "./TeamPage";
import { ThemeToggle } from "./ThemeToggle";
import { type View, useURLState } from "./useURLState";

// standingsPageFor dispatches the Standings tab to the right page
// per sport. Soccer stays on RPIPage (RPI is the ranking of record);
// football has no RPI so it ships records + optional subdivision
// filter; basketball-{men,women} share one NET + Quadrants page
// against two dedicated backend routes. Unknown sports fall through
// to the soccer RPI page as a safe default — the dropdown already
// restricts input to known sports.
function standingsPageFor(sport: string): "rpi" | "football" | "basketball" {
  if (sport === "football") return "football";
  if (sport === "basketball-men" || sport === "basketball-women") {
    return "basketball";
  }
  return "rpi";
}

const PAGES: { id: View; label: string }[] = [
  { id: "home", label: "Home" },
  { id: "schedule", label: "Schedule" },
  { id: "rpi", label: "Standings" },
  { id: "conferences", label: "Conferences" },
];

export function App() {
  // View, sport, and division are all URL-backed so a shared link
  // takes the recipient straight to the right tab + sport + division
  // without them needing to click after load. useURLState reads the
  // initial state from window.location, pushes updates to history on
  // change, and syncs on back/forward. Defaults match what the user
  // sees when visiting / — Today, Women's Soccer, D1.
  const {
    view,
    setView,
    sport,
    setSport,
    division,
    setDivision,
    mode: standingsMode,
    setMode: setStandingsMode,
    conference: standingsConference,
    setConference: setStandingsConference,
    date,
    setDate,
    teamSlug,
  } = useURLState();

  return (
    <main>
      {/* Skip link lets keyboard users jump past the header + nav
          straight to the active view. Visible only on focus — becomes
          a real button when a user tabs to it from the top of the
          page. */}
      <a href="#main-content" className="skip-link">
        Skip to main content
      </a>
      <header>
        <div className="header-left">
          <h1>The Cutline</h1>
          <nav role="navigation" aria-label="Primary">
            {PAGES.map((p) => (
              <button
                key={p.id}
                type="button"
                aria-current={view === p.id ? "page" : undefined}
                className={view === p.id ? "active" : undefined}
                onClick={() => setView(p.id)}
              >
                {p.label}
              </button>
            ))}
          </nav>
        </div>
        <div className="header-right">
          <label className="header-select">
            <span className="visually-hidden">Sport</span>
            <select
              value={sport}
              onChange={(e) => setSport(e.target.value)}
              aria-label="Sport"
            >
              <option value="soccer-women">Women's Soccer</option>
              <option value="soccer-men">Men's Soccer</option>
              <option value="football">Football</option>
              <option value="basketball-women">Women's Basketball</option>
              <option value="basketball-men">Men's Basketball</option>
            </select>
          </label>
          <label className="header-select">
            <span className="visually-hidden">Division</span>
            <select
              value={division}
              onChange={(e) => setDivision(e.target.value)}
              aria-label="Division"
            >
              <option value="d1">D1</option>
              <option value="d2">D2</option>
              <option value="d3">D3</option>
            </select>
          </label>
          <ThemeToggle />
        </div>
      </header>
      <div id="main-content" tabIndex={-1}>
        {view === "home" && <HomePage />}
        {view === "schedule" && (
          <ScoreboardPage
            sport={sport}
            division={division}
            date={date}
            onDateChange={setDate}
            conference={standingsConference}
            onConferenceChange={setStandingsConference}
          />
        )}
        {view === "rpi" && standingsPageFor(sport) === "rpi" && (
          <RPIPage
            sport={sport}
            division={division}
            mode={standingsMode}
            onModeChange={setStandingsMode}
            conference={standingsConference}
            onConferenceChange={setStandingsConference}
          />
        )}
        {view === "rpi" && standingsPageFor(sport) === "football" && (
          <FootballStandingsPage
            sport={sport}
            division={division}
            conference={standingsConference}
            onConferenceChange={setStandingsConference}
          />
        )}
        {view === "rpi" && standingsPageFor(sport) === "basketball" && (
          <BasketballStandingsPage
            sport={sport}
            division={division}
            conference={standingsConference}
            onConferenceChange={setStandingsConference}
          />
        )}
        {view === "conferences" && (
          <ConferencesPage sport={sport} division={division} />
        )}
        {view === "team" && teamSlug !== "" && (
          <TeamPage sport={sport} division={division} slug={teamSlug} />
        )}
      </div>
      <footer className="site-footer">
        brought to you by the obsessive mind of{" "}
        <a href="https://omarcrosby.com/">Omar Crosby</a>
      </footer>
    </main>
  );
}
